import React, { useMemo } from 'react';
import moment from 'moment';
import { Typography, Button, Box, ListItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LockRounded } from '@mui/icons-material';
import actions from '@src/redux/actions';
import useUserInfo from '@src/hooks/useTtsUser';
import apis from '@src/apis';
import MigratedIcon from '@src/assets/icons/migrate.svg';
import i18n from '@src/languages';
import { StyledList, StyledMigratedDubbingDialog } from './index.style';

const MigratedDubbingDialog = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { language } = i18n;
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();
  const {
    oldRemainingCharacters = 0,
    oldLockCharacters = 0,
    oldRemainingSeconds = 0,
    oldLockSeconds = 0,
  } = ttsUser?.migratedDubbing || {};

  const oldRemainingSecondsToMinutes =
    (oldRemainingSeconds && Math.floor(oldRemainingSeconds / 60)) || 0;
  const oldLockSecondsToMinutes =
    (oldLockSeconds && Math.floor(oldLockSeconds / 60)) || 0;

  const migratedRemainingCharacters =
    (oldRemainingSeconds && Math.ceil((oldRemainingSeconds / 60) * 700)) || 0;
  const migratedLockCharacters =
    (oldLockSeconds && Math.ceil((oldLockSeconds / 60) * 700)) || 0;

  const newRemainingCharacters = user?.remainingCharacters || 0;
  const newLockCharacters = user?.lockCharacters || 0;

  const handleClickAgreeToMigrate = async () => {
    try {
      await apis.user.agreeToMigrateDubbingToStudio();
      dispatch(
        actions.user.getTtsUserSuccess({ ...ttsUser, migratedDubbing: null }),
      );
      onClose();
      return true;
    } catch (error) {
      return null;
    }
  };

  const isPackageExpired = useMemo(() => {
    if (ttsUser?.packageExpiryDate) {
      return new Date(ttsUser.packageExpiryDate) < new Date();
    }
    return true;
  }, [ttsUser]);

  return (
    <StyledMigratedDubbingDialog open={open} fullWidth>
      <Box className="dialog-container">
        <Box className="dialog-title">
          <img src={MigratedIcon} alt="migrated" />
        </Box>

        <Typography className="title">
          {t('announcement').toUpperCase()}
        </Typography>
        <Typography className="message">
          {t('dubbingMigrateMessage')}
        </Typography>
        <Typography
          className="conversion-info"
          dangerouslySetInnerHTML={{
            __html: t('dubbingMigrateSeconds', {
              minutes: (oldRemainingSecondsToMinutes > 0
                ? oldRemainingSecondsToMinutes
                : oldLockSecondsToMinutes
              )?.toLocaleString(language),
              characters: (migratedRemainingCharacters > 0
                ? migratedRemainingCharacters
                : migratedLockCharacters
              )?.toLocaleString(language),
            }),
          }}
        />
        <Typography className="conversion-rate">
          {t('dubbingMigrateRatio')}
        </Typography>
        <Box className="details-box">
          <Typography className="migrate-title">
            {t('dubbingMigrateTitle')}
          </Typography>
          <StyledList>
            <ListItem className="new-characters">
              <Typography
                className="new-characters"
                component="span"
                dangerouslySetInnerHTML={{
                  __html: t('newRemainingCharacters', {
                    newCharacters: (newRemainingCharacters > 0
                      ? newRemainingCharacters
                      : newLockCharacters
                    )?.toLocaleString(language),
                  }),
                }}
              />
              {isPackageExpired && <LockRounded className="lock-icon" />}
            </ListItem>
            <Typography className="migrate-info">
              {`${(oldRemainingCharacters > 0
                ? oldRemainingCharacters
                : oldLockCharacters
              )?.toLocaleString(language)} (${t(
                'remainingCharacter',
              ).toLocaleLowerCase()}) + ${(migratedRemainingCharacters > 0
                ? migratedRemainingCharacters
                : migratedLockCharacters
              )?.toLocaleString(language)} (${t('characterConverted')})`}
            </Typography>

            <ListItem
              className={isPackageExpired ? 'expired' : 'package-expiry-date'}
            >
              {t('expiryDate')}:{' '}
              <b>
                {moment(ttsUser?.packageExpiryDate).format(
                  'DD/MM/YYYY - HH:mm',
                )}
              </b>
              {isPackageExpired && ` (${t('expired').toLocaleLowerCase()})`}
            </ListItem>
            {isPackageExpired && (
              <ListItem className="package-preserver-date">
                <span className="package-preserver-date">
                  {t('preservationExpiryDate')}:{' '}
                  <b>
                    {user?.preservationExpiryDate &&
                      moment(user?.preservationExpiryDate).format(
                        'DD/MM/YYYY - HH:mm',
                      )}
                  </b>
                </span>
              </ListItem>
            )}
          </StyledList>
        </Box>

        <Box className="button-agree">
          <Button
            className="button"
            variant="contained"
            onClick={handleClickAgreeToMigrate}
          >
            {t('understoodV3')}
          </Button>
        </Box>
      </Box>
    </StyledMigratedDubbingDialog>
  );
};

export default MigratedDubbingDialog;
